import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { productDetails } from '../../utils/productData';
import { 
  ArrowLeft, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles,
  Palette,
  Leaf,
  FileText,
  Tag,
  Recycle,
  ClipboardCheck,
  Scissors,
  Shield,
  Package,
  Gift,
  Globe
} from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';

const iconMap = {
  'sparkles': Sparkles, 'palette': Palette, 'leaf': Leaf,
  'file-text': FileText, 'tag': Tag, 'recycle': Recycle,
  'clipboard-check': ClipboardCheck, 'scissors': Scissors,
  'shield': Shield, 'package': Package, 'gift': Gift, 'globe': Globe
};

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const product = productDetails[slug];
  
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="product-not-found-title">
            {language === 'tr' ? 'Ürün Bulunamadı' : 'Product Not Found'}
          </h1>
          <Link to="/" className="text-blue-600 hover:underline" data-testid="back-home-link">
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return <ProductDetailContent key={slug} slug={slug} product={product} language={language} />;
};

const ProductDetailContent = ({ slug, product, language }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const thumbRef = useRef(null);
  const content = product[language];
  const totalImages = product.images.length;

  useEffect(() => {
    if (!product.images?.length) return;
    product.images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [product.images]);

  const nextImage = useCallback(() => {
    setSelectedImage((prev) => (prev + 1) % totalImages);
  }, [totalImages]);

  const prevImage = useCallback(() => {
    setSelectedImage((prev) => (prev - 1 + totalImages) % totalImages);
  }, [totalImages]);

  useEffect(() => {
    if (thumbRef.current) {
      const container = thumbRef.current;
      const el = container.children[selectedImage];
      if (el) {
        const left = el.offsetLeft - container.offsetLeft - (container.clientWidth / 2) + (el.clientWidth / 2);
        container.scrollTo({ left, behavior: 'smooth' });
      }
    }
  }, [selectedImage]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-x-hidden">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1637004732258-4b792ce8f474?w=1600&q=70&fm=webp" alt="Custom garment labels and tags manufactured by Etiteks in Istanbul" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <Link to="/" className="inline-flex items-center text-blue-200 hover:text-white transition-colors mb-8 group" data-testid="breadcrumb-home">
            <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            {language === 'tr' ? 'Ana Sayfa' : 'Home'}
          </Link>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="product-title">{content.title}</h1>
          <p className="text-xl text-blue-100 max-w-3xl" data-testid="product-subtitle">{content.subtitle}</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            
            {/* Image Gallery */}
            {totalImages > 0 ? (
            <div className="space-y-4 min-w-0">
              <div 
                className={`relative ${slug === 'packaging' ? 'aspect-square' : 'aspect-[4/3]'} rounded-2xl overflow-hidden shadow-2xl group bg-gray-900`}
                data-testid="main-product-image"
              >
                <img
                  src={product.images[selectedImage]}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60"
                />
                <img
                  src={product.images[selectedImage]}
                  alt={content.title}
                  className="relative w-full h-full object-contain drop-shadow-lg"
                />
                <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors" data-testid="gallery-prev-btn" aria-label="Previous image">
                  <ChevronLeft size={24} className="text-gray-800" />
                </button>
                <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors" data-testid="gallery-next-btn" aria-label="Next image">
                  <ChevronRight size={24} className="text-gray-800" />
                </button>
                <div className="absolute bottom-4 right-4 bg-black/60 text-white text-sm px-3 py-1 rounded-full font-medium" data-testid="image-counter">
                  {selectedImage + 1} / {totalImages}
                </div>
              </div>
              
              {/* Thumbnails */}
              <div className="relative">
                <div ref={thumbRef} className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#9ca3af transparent' }} data-testid="thumbnail-strip">
                  {product.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index 
                          ? 'border-gray-800 shadow-lg ring-2 ring-gray-800/20' 
                          : 'border-transparent hover:border-gray-300 opacity-70 hover:opacity-100'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img src={img} alt={`${product.en.title} by Etiteks – product photo ${index + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            ) : (
            <div className={`${slug === 'packaging' ? 'aspect-square' : 'aspect-[4/3]'} rounded-2xl bg-gray-100 flex items-center justify-center`}>
              <p className="text-gray-400 text-lg">{language === 'tr' ? 'Görseller yakında eklenecek' : 'Images coming soon'}</p>
            </div>
            )}

            {/* Product Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{language === 'tr' ? 'Genel Bakış' : 'Overview'}</h2>
                <p className="text-lg text-gray-600 leading-relaxed" data-testid="product-overview">{content.description}</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{language === 'tr' ? 'Özellikler' : 'Features'}</h3>
                <ul className="space-y-3" data-testid="product-features">
                  {content.features.map((feature, index) => (
                    <li key={index} className="flex items-start" data-testid={`product-feature-${index + 1}`}>
                      <span className="flex-shrink-0 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="w-2 h-2 bg-gray-700 rounded-full" />
                      </span>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{language === 'tr' ? 'Teknik Özellikler' : 'Technical Specifications'}</h3>
                <div className="space-y-5" data-testid="product-technical-specs">
                  {Object.entries(content.technicalSpecs).map(([category, items], index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-5">
                      <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">{category}</h4>
                      <div className="flex flex-wrap gap-2">
                        {items.map((item, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-white text-gray-700 rounded-lg text-sm font-medium border border-gray-200">{item}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-6 border-t border-gray-200">
                <Link to="/contact" className="inline-flex items-center px-8 py-4 bg-gray-800 text-white font-semibold rounded-xl hover:bg-gray-900 transition-colors shadow-lg hover:shadow-xl" data-testid="get-quote-btn">
                  {language === 'tr' ? 'Teklif Alın' : 'Get a Quote'}
                  <ArrowLeft size={20} className="ml-2 rotate-180" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {content.highlights.map((highlight, index) => {
              const IconComponent = iconMap[highlight.icon] || Sparkles;
              return (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow" data-testid={`product-highlight-${index + 1}`}>
                  <div className="w-14 h-14 border-2 border-gray-300 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent size={28} className="text-gray-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-testid={`product-highlight-title-${index + 1}`}>{highlight.title}</h3>
                  <p className="text-gray-600" data-testid={`product-highlight-description-${index + 1}`}>{highlight.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
